import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Spin, Tabs, TabsProps } from "antd";
import { ProjectContext, ProjectSettingType } from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DateFilter, DateUtils } from "utils/dateutils";
import {
  IdLinkComponent,
  logPageIDLinkCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { SubmittalStatusToStrMap } from "constants/index";
import "./action-items.scss";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { MaterialCiqIcon, SubmittalCiqIcon } from "components/svg-icons";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import ErrorBoundary from "components/error-boundary";
import { linkCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";
import { riskLevelComparator } from "utils/grid-utils";
import {
  ActionItemType,
  FilterNamesEnum,
  GcDashBoardType,
  NavigationPageEnum
} from "../../models";

enum ActionItemViewEnum {
  Submittals,
  Materials,
  BidPackage
}

function ActionItems({
  projectId,
  dashboardData,
  projectDetails,
  goToFiltered,
  hasBidPackageAccess
}: {
  projectId: string;
  dashboardData: GcDashBoardType | undefined;
  projectDetails: ProjectSettingType | undefined;
  goToFiltered: (page: NavigationPageEnum, filter: FilterNamesEnum) => void;
  hasBidPackageAccess: boolean;
}) {
  const [isGridReady, setGridReady] = useState(false);
  const {
    columnHeaders: {
      materialHeaderMap,
      submittalHeaderMap,
      bidPackageHeaderMap
    },
    isFeatureFlagEnabled
  } = useContext(ProjectContext);
  const isBuyoutFeatureEnabled = isFeatureFlagEnabled("BUYOUT");

  const gridRef = useRef<AgGridReact>(null);
  const [selectedView, setSelectedView] = useState<ActionItemViewEnum>(
    ActionItemViewEnum.Materials
  );

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: false,
      resizable: true
    };
  }, []);

  const columnDefsSubmittal: ColDef[] = useMemo(
    () => [
      {
        colId: "submittal_id",
        field: "submittal_id",
        headerName: submittalHeaderMap?.submittal_id || "",
        headerTooltip: submittalHeaderMap?.submittal_id || "",
        width: 100,
        cellRenderer: IdLinkComponent,
        cellRendererParams: {
          projectId
        },
        tooltipValueGetter: ({ data }) =>
          projectDetails?.spec_section && data.spec_no
            ? `${data.spec_no} - ${data.submittal_id}`
            : `${data.submittal_id}`,
        comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
          submittalSequenceComparator(
            valueA,
            valueB,
            nodeA,
            nodeB,
            projectDetails?.spec_section
          )
      },
      {
        colId: "title",
        field: "title",
        headerName: submittalHeaderMap?.title || "",
        headerTooltip: submittalHeaderMap?.title || "",
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/submittals/${params.data.id}`
          );
        }
      },
      {
        colId: "status",
        field: "workflow_status",
        headerName: submittalHeaderMap?.workflow_status || "",
        headerTooltip: submittalHeaderMap?.workflow_status || "",
        valueGetter: ({ data }) =>
          SubmittalStatusToStrMap[data.workflow_status],
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "next_planned_deadline",
        field: "next_planned_deadline",
        sort: "asc",
        headerName: submittalHeaderMap?.next_planned_deadline || "",
        headerTooltip: submittalHeaderMap?.next_planned_deadline || "",
        comparator: DateFilter.comparator,
        cellRenderer: nextDeadlineCellRenderer,
        valueGetter: ({ data }: any) =>
          data.next_planned_deadline
            ? DateUtils.format(data.next_planned_deadline)
            : "",
        width: 160
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: submittalHeaderMap?.risk_level || "",
        headerTooltip: submittalHeaderMap?.risk_level || "",
        cellRenderer: riskCellRenderer,
        comparator: riskLevelComparator,
        filter: true,
        width: 110
      },
      {
        field: "submitter_reviewer",
        colId: "submitter",
        headerName: submittalHeaderMap?.submitter || "",
        headerTooltip: submittalHeaderMap?.submitter || "",
        filter: true,
        cellRenderer: truncatedTextCellRenderer,
        valueGetter: ({ data }) => {
          if (data.submitter_reviewer) {
            return `${data.submitter_reviewer?.first_name} ${data.submitter_reviewer?.last_name}`;
          }
          return "";
        }
      },
      {
        colId: "gc_reviewer",
        headerName: submittalHeaderMap?.gc_reviewer || "",
        headerTooltip: submittalHeaderMap?.gc_reviewer || "",
        filter: true,
        valueGetter: ({ data }) => {
          if (data.gc_reviewer_user) {
            return `${data.gc_reviewer_user?.first_name} ${data.gc_reviewer_user?.last_name}`;
          }
          return "";
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ],
    [submittalHeaderMap, projectId, projectDetails?.spec_section]
  );

  const columnDefsMaterial: ColDef[] = useMemo(
    () => [
      {
        field: "material_id",
        colId: "material_id",
        headerName: materialHeaderMap?.material_id || "",
        headerTooltip: materialHeaderMap?.material_id || "",
        width: 80,
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/materials/${params.data.id}`
          );
        }
      },
      {
        colId: "name",
        field: "name",
        headerName: materialHeaderMap?.name || "",
        headerTooltip: materialHeaderMap?.name || "",
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/materials/${params.data.id}`
          );
        }
      },
      {
        field: "status",
        colId: "status",
        headerName: materialHeaderMap?.status || "",
        headerTooltip: materialHeaderMap?.status || "",
        width: 110,
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "next_planned_deadline",
        field: "next_planned_deadline",
        sort: "asc",
        headerName: materialHeaderMap?.next_planned_deadline || "",
        comparator: DateFilter.comparator,
        cellRenderer: nextDeadlineCellRenderer,
        valueGetter: ({ data }: any) =>
          data.next_planned_deadline
            ? DateUtils.format(data.next_planned_deadline)
            : "",
        width: 160
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: materialHeaderMap?.risk_level || "",
        headerTooltip: materialHeaderMap?.risk_level || "",
        cellRenderer: riskCellRenderer,
        comparator: riskLevelComparator,
        filter: true,
        width: 110
      },
      {
        colId: "assignee",
        field: "assignee_user",
        headerName: materialHeaderMap?.assignee || "",
        headerTooltip: materialHeaderMap?.assignee || "",
        filter: true,
        cellRenderer: truncatedTextCellRenderer,
        valueGetter: ({ data }) => {
          if (data.assignee_user) {
            return `${data.assignee_user?.first_name} ${data.assignee_user?.last_name}`;
          }
          return "";
        }
      },
      {
        colId: "gc_representative",
        headerName: materialHeaderMap?.gc_representative || "",
        headerTooltip: materialHeaderMap?.gc_representative || "",
        filter: true,
        valueGetter: ({ data }) => {
          if (data.gc_representative_user) {
            return `${data.gc_representative_user?.first_name} ${data.gc_representative_user?.last_name}`;
          }
          return "";
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ],
    [materialHeaderMap, projectId]
  );

  const columnDefsBidPackage: ColDef[] = useMemo(
    () => [
      {
        field: "auto_inc_value",
        headerName: bidPackageHeaderMap?.id,
        lockPosition: "left",
        width: 100,
        cellRenderer: linkCellRenderer,
        cellRendererParams: {
          projectId,
          featureKey: "bid-packages"
        }
      },
      {
        field: "number",
        colId: "number",
        tooltipField: "number",
        headerName: bidPackageHeaderMap?.number,
        headerTooltip: bidPackageHeaderMap?.number,
        cellRenderer: linkCellRenderer,
        cellRendererParams: {
          projectId,
          featureKey: "bid-packages"
        }
      },
      {
        colId: "title",
        field: "title",
        headerName: bidPackageHeaderMap?.title,
        headerTooltip: bidPackageHeaderMap?.title,
        tooltipField: "title",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "milestone_state",
        colId: "milestone_state",
        tooltipField: "milestone_state",
        headerName: bidPackageHeaderMap?.workflow_status,
        headerTooltip: bidPackageHeaderMap?.workflow_status,
        width: 110,
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "next_planned_deadline",
        field: "next_planned_deadline",
        sort: "asc",
        headerName: bidPackageHeaderMap?.next_deadline,
        comparator: DateFilter.comparator,
        cellRenderer: nextDeadlineCellRenderer,
        valueGetter: ({ data }: any) =>
          data.next_planned_deadline
            ? DateUtils.format(data.next_planned_deadline)
            : "",
        width: 160
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: bidPackageHeaderMap?.risk_level,
        headerTooltip: bidPackageHeaderMap?.risk_level,
        cellRenderer: riskCellRenderer,
        comparator: riskLevelComparator,
        filter: true,
        width: 110
      }
    ],
    [projectId, bidPackageHeaderMap]
  );

  const [gridData, setGridData] = useState<{
    rowData: ActionItemType[] | undefined;
    defaultColDef: ColDef;
    columnDefs: ColDef[];
  }>({
    rowData: undefined,
    defaultColDef,
    columnDefs: columnDefsSubmittal
  });

  useEffect(() => {
    const setData = (rowData: ActionItemType[], columnDefs: ColDef[]) => {
      setGridData((pre) => ({
        ...pre,
        rowData,
        columnDefs
      }));
    };

    if (dashboardData) {
      if (selectedView === ActionItemViewEnum.Submittals) {
        setData(dashboardData.submittals.list, columnDefsSubmittal);
      }
      if (selectedView === ActionItemViewEnum.Materials) {
        setData(dashboardData.materials.list, columnDefsMaterial);
      }
      if (selectedView === ActionItemViewEnum.BidPackage) {
        setData(dashboardData.bid_packages.list, columnDefsBidPackage);
      }
    }
  }, [
    columnDefsBidPackage,
    columnDefsMaterial,
    columnDefsSubmittal,
    dashboardData,
    selectedView
  ]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isGridReady]);

  const agGrid = useMemo(
    () => (
      <ErrorBoundary>
        <div className="h-80 grow flex-col ag-theme-alpine ">
          <AgGridReact<ActionItemType>
            key={selectedView.toString()}
            ref={gridRef}
            rowData={gridData.rowData}
            defaultColDef={gridData.defaultColDef}
            columnDefs={gridData.columnDefs}
            headerHeight={36}
            onGridReady={(ref) => {
              ref.api.sizeColumnsToFit();
              setGridReady(true);
            }}
            onRowDataUpdated={() => {
              gridRef.current?.api?.sizeColumnsToFit();
            }}
            loadingOverlayComponent={Spin}
            overlayNoRowsTemplate="You have no actions due in 2 weeks."
            context={{
              isCurrentUserGC: true,
              projectDetails
            }}
            suppressContextMenu
            tooltipShowMode="whenTruncated"
          />
        </div>
      </ErrorBoundary>
    ),
    [
      selectedView,
      gridData.rowData,
      gridData.defaultColDef,
      gridData.columnDefs,
      projectDetails
    ]
  );

  const bidPackageTab = isBuyoutFeatureEnabled && hasBidPackageAccess;

  const items: TabsProps["items"] = [
    ...(bidPackageTab
      ? [
          {
            key: ActionItemViewEnum.BidPackage.toString(),
            label: (
              <div className="flex items-center space-x-2">
                <BidPackageCiqIcon
                  size={14}
                  className={
                    selectedView === ActionItemViewEnum.BidPackage
                      ? "fill-active"
                      : "fill-black"
                  }
                  opacity="1"
                />
                <div>Bid Package</div>
                <div className="border border-solid rounded-full px-2 border-ciq-gray-300 text-black text-opacity-50">
                  {dashboardData ? dashboardData?.bid_packages?.count : ""}
                </div>
              </div>
            ),
            children: agGrid
          }
        ]
      : []),
    {
      key: ActionItemViewEnum.Submittals.toString(),
      label: (
        <div className="flex items-center space-x-2">
          <SubmittalCiqIcon
            size={14}
            className={
              selectedView === ActionItemViewEnum.Submittals
                ? "fill-active"
                : "fill-black"
            }
            opacity="1"
          />
          <div>Submittals</div>
          <div className="border border-solid rounded-full px-2 border-ciq-gray-300 text-black text-opacity-50">
            {dashboardData ? dashboardData?.submittals?.count : ""}
          </div>
        </div>
      ),
      children: agGrid
    },
    {
      key: ActionItemViewEnum.Materials.toString(),
      label: (
        <div className="flex items-center space-x-2">
          <MaterialCiqIcon
            size={14}
            className={
              selectedView === ActionItemViewEnum.Materials
                ? "fill-active"
                : "fill-black"
            }
            opacity="1"
          />
          <div>Materials</div>
          <div className="border border-solid rounded-full px-2 border-ciq-gray-300 text-black text-opacity-50">
            {dashboardData ? dashboardData?.materials?.count : ""}
          </div>
        </div>
      ),
      children: agGrid
    }
  ];

  return (
    <div className="box-border flex flex-col">
      <div className="flex w-full justify-between items-center box-header bottom-border !p-6">
        <div>Actions due in 2 weeks</div>

        {gridData.rowData && gridData.rowData?.length > 0 && (
          <Button
            type="link"
            className="p-0 h-fit"
            onClick={() => {
              if (selectedView === ActionItemViewEnum.Submittals)
                goToFiltered(
                  NavigationPageEnum.SubmittalLog,
                  FilterNamesEnum.SCDueIn2Week
                );
              if (selectedView === ActionItemViewEnum.Materials)
                goToFiltered(
                  NavigationPageEnum.MaterialLog,
                  FilterNamesEnum.SCDueIn2Week
                );
            }}
            data-testid="index-button-view-in-log"
          >
            View in Log
          </Button>
        )}
      </div>
      <Tabs
        defaultActiveKey={selectedView.toString()}
        className="px-5 pt-0 pb-5"
        items={items}
        onChange={(key) => {
          const actionKey = Number(key) as ActionItemViewEnum;

          switch (actionKey) {
            case ActionItemViewEnum.BidPackage:
              setSelectedView(ActionItemViewEnum.BidPackage);
              break;
            case ActionItemViewEnum.Submittals:
              setSelectedView(ActionItemViewEnum.Submittals);
              break;
            default:
              setSelectedView(ActionItemViewEnum.Materials);
              break;
          }
        }}
        data-testid="index-tabs-action-items"
      />
    </div>
  );
}

export default ActionItems;
